import { useEffect, useLayoutEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useStore } from '@/store/useStore';
import { HabitTemplate } from '@/types';
import LandingPage from '@/pages/LandingPage';
import OnboardingPage from '@/pages/OnboardingPage';
import TrackerPage from '@/pages/TrackerPage';
import CoachPage from '@/pages/CoachPage';
import SettingsPage from '@/pages/SettingsPage';
import StatsPage from '@/pages/StatsPage';
import Footer from '@/components/Footer';
import AppHeader from '@/components/AppHeader';
import { useNotifications } from '@/hooks/useNotifications';

// Template file names
const TEMPLATE_FILES = [
  'fitness-30days',
  'fitness-strength',
  'fitness-steps',
  'fitness-running',
  'sleep-better',
  'sleep-schedule',
  'sleep-detox',
  'mindfulness-meditation',
  'mindfulness-stress',
  'mindfulness-gratitude',
  'mindfulness-journaling',
  'learning-reading',
  'learning-skills',
  'learning-language',
  'money-budget',
  'money-saving',
  'money-debt',
  'cyber-basics',
  'cyber-passwords',
  'cyber-privacy',
  'cyber-family',
  'productivity-pomodoro',
  'productivity-morning',
  'nutrition-healthy',
  'nutrition-mindful',
  'social-connection',
  'social-presence',
];

function App() {
  const { settings, setTemplates } = useStore();
  const location = useLocation();
  const hasCompletedOnboarding = settings.onboardingComplete;

  // Initialize notification system
  useNotifications();

  // Scroll to top on route change
  useLayoutEffect(() => {
    // Temporarily disable smooth scroll for instant navigation
    const html = document.documentElement;
    const originalBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';

    window.scrollTo(0, 0);
    html.scrollTop = 0;

    // Restore after a frame
    requestAnimationFrame(() => {
      html.style.scrollBehavior = originalBehavior;
    });
  }, [location.pathname]);

  // Show header on app pages (not landing or onboarding)
  const showHeader = hasCompletedOnboarding &&
    !['/onboarding', '/'].includes(location.pathname);

  // Load templates on mount
  useEffect(() => {
    async function loadTemplates() {
      const templates: HabitTemplate[] = [];
      // Use relative path that works in both dev and prod
      const basePath = import.meta.env.BASE_URL || '/goodies/easyhabits/';

      for (const file of TEMPLATE_FILES) {
        try {
          const response = await fetch(`${basePath}habits/${file}.json`);
          if (response.ok) {
            const template = await response.json();
            templates.push(template);
          }
        } catch (e) {
          console.warn(`Failed to load template: ${file}`, e);
        }
      }

      setTemplates(templates);
    }

    loadTemplates();
  }, [setTemplates]);

  const basePath = import.meta.env.BASE_URL || '/goodies/easyhabits/';

  return (
    <div className="min-h-full relative">
      {/* Beach Background - shared across all pages */}
      <div className="fixed inset-0 z-0">
        <img
          src={`${basePath}images/beach.jpg`}
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-[center_70%]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-white/80 to-amber-50/90" />
      </div>

      {/* Mobile-width container */}
      <div className="relative z-10 min-h-full max-w-md mx-auto shadow-xl">
        {showHeader && <AppHeader />}
        <Routes>
        <Route
          path="/"
          element={
            hasCompletedOnboarding ? (
              <Navigate to="/coach" replace />
            ) : (
              <LandingPage />
            )
          }
        />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route
          path="/tracker"
          element={
            hasCompletedOnboarding ? (
              <TrackerPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/coach"
          element={
            hasCompletedOnboarding ? (
              <CoachPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/settings"
          element={
            hasCompletedOnboarding ? (
              <SettingsPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/stats"
          element={
            hasCompletedOnboarding ? (
              <StatsPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
      </Routes>
      </div>

      <Footer />
    </div>
  );
}

export default App;
